import React, { useState, useEffect } from "react";
import { IconButton, Popper, Box, Tooltip } from "@mui/material";
import { ChevronLeft, ChevronRight, Info } from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import api from "../../services/api";
import { obterIdTenent } from "../../services/seguranca";
import { useAppSelector } from "../../store/hooks";
import { formatarParaReais } from "../../services/utilitarios";

dayjs.locale("pt-br");

interface Recebimento {
  DataPrevista: string;
  Valor: number;
  Status: "Pago" | "A vencer" | "Em atraso";
}

interface LegendaItem {
  status: Recebimento["Status"];
  cor: string;
}

interface CalendarioRecebimentosProps {
  legenda?: LegendaItem[];
}

const defaultStatusColors: Record<Recebimento["Status"], string> = {
  Pago: "#4CAF50",
  "A vencer": "#FFC107",
  "Em atraso": "#F44336",
};

const explicacoes: Record<Recebimento["Status"], string> = {
  Pago: "Recebimentos já confirmados.",
  "A vencer": "Recebimentos previstos para o futuro.",
  "Em atraso": "Recebimentos que estão vencidos.",
};

export const CalendarioRecebimentos: React.FC<CalendarioRecebimentosProps> = ({
  legenda = Object.entries(defaultStatusColors).map(([status, cor]) => ({
    status: status as Recebimento["Status"],
    cor,
  })),
}) => {
  const token = useAppSelector((state) => state.token);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [recebimentos, setRecebimentos] = useState<Recebimento[]>([]);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverData, setPopoverData] = useState<{
    date: dayjs.Dayjs;
    resumo: Record<Recebimento["Status"], { total: number; count: number }>;
  } | null>(null);
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const statusColors = legenda.reduce(
    (acc, { status, cor }) => {
      acc[status] = cor;
      return acc;
    },
    {} as Record<Recebimento["Status"], string>
  );

  const handleDayClick = (date: dayjs.Dayjs) => {
    const dataFormatada = date.format('YYYY-MM-DD');
    const urlMontada = `/pagamento?dataInicio=${dataFormatada}&dataFim=${dataFormatada}&status=-1`;
    window.open(urlMontada, '_blank');
  };

  const fetchRecebimentos = async () => {
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 250));
      const response = await api.get(
        `Pagamento/ParaCalendario/${obterIdTenent(token)}/${currentMonth.format(
          "YYYY-MM-DD"
        )}`
      );
      const formattedData = response.data.map((item: any) => ({
        DataPrevista: item.data,
        Valor: item.valor,
        Status: item.status,
      }));
      setRecebimentos(formattedData);
    } catch (error) {
      console.error("Erro ao buscar recebimentos:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecebimentos();
  }, [currentMonth]);

  const startOfMonth = currentMonth.startOf("month");
  const startDay = startOfMonth.day();
  const daysInMonth = currentMonth.daysInMonth();

  const dias = Array.from({ length: startDay + daysInMonth }, (_, index) => {
    if (index < startDay) return null;
    return startOfMonth.add(index - startDay, "day");
  });

  const handlePrevMonth = () => setCurrentMonth(currentMonth.subtract(1, "month"));
  const handleNextMonth = () => setCurrentMonth(currentMonth.add(1, "month"));

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    date: dayjs.Dayjs,
    eventos: Recebimento[]
  ) => {
    const resumo = eventos.reduce((acc, evento) => {
      if (!acc[evento.Status]) acc[evento.Status] = { total: 0, count: 0 };
      acc[evento.Status].total += evento.Valor;
      acc[evento.Status].count += 1;
      return acc;
    }, {} as Record<Recebimento["Status"], { total: number; count: number }>);

    setPopoverData({ date, resumo });
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPopoverData(null);
    setAnchorEl(null);
  };

  const handleDayHover = (index: number) => {
    setHoveredDay(index);
  };

  const handleDayLeave = () => {
    setHoveredDay(null);
  };

  // Função para agrupar recebimentos por status e limitar a exibição
  const getEventosAgrupados = (eventos: Recebimento[]) => {
    const agrupados = eventos.reduce((acc, evento) => {
      if (!acc[evento.Status]) acc[evento.Status] = 0;
      acc[evento.Status]++;
      return acc;
    }, {} as Record<Recebimento["Status"], number>);

    return Object.entries(agrupados).map(([status, count]) => ({
      status: status as Recebimento["Status"],
      count,
    }));
  };

  return (
    <div
      style={{
        fontFamily: "'Segoe UI', Roboto, sans-serif",
        padding: "16px",
        width: "100%",
        maxWidth: "100%",
        margin: "0 auto",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <h2 style={{ marginBottom: 4, color: "#1f4680", fontSize: "20px" }}>
        Calendário de Recebimentos
      </h2>
      <p style={{ marginTop: 0, color: "#555", fontSize: "14px" }}>
        Acompanhe as entradas recebidas, em atraso e a vencer da sua locadora
      </p>

      {/* Legenda - Responsiva */}
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          overflow: "hidden",
        }}
      >
        {legenda.map(({ status, cor }) => (
          <Tooltip key={status} title={explicacoes[status]} arrow>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: cor,
                  borderRadius: 3,
                }}
              />
              <span style={{ fontWeight: 500, fontSize: 13 }}>{status}</span>
              <Info
                style={{
                  fontSize: 14,
                  color: "#1f4680",
                  opacity: 0.7,
                }}
              />
            </div>
          </Tooltip>
        ))}
      </div>

      {/* Cabeçalho mês/ano - Ajustado para responsividade */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
          backgroundColor: "#f5f9ff",
          padding: "6px 12px",
          borderRadius: 6,
          width: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <IconButton
          onClick={handlePrevMonth}
          disabled={loading}
          sx={{
            color: "#1f4680",
            padding: "6px",
            minWidth: "auto",
          }}
          size="small"
        >
          <ChevronLeft size={18} />
        </IconButton>
        <h3
          style={{
            margin: 0,
            color: "#1f4680",
            fontSize: "16px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            padding: "0 8px",
          }}
        >
          {currentMonth.format("MMMM YYYY")}
        </h3>
        <IconButton
          onClick={handleNextMonth}
          disabled={loading}
          sx={{
            color: "#1f4680",
            padding: "6px",
            minWidth: "auto",
          }}
          size="small"
        >
          <ChevronRight size={18} />
        </IconButton>
      </div>

      {/* Dias da semana - Responsivo */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 8,
          textAlign: "center",
          marginBottom: 8,
          width: "100%",
          overflow: "hidden",
        }}
      >
        {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((dia) => (
          <strong
            key={dia}
            style={{
              color: "#1f4680",
              padding: "6px 0",
              fontSize: 12,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {dia}
          </strong>
        ))}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 8,
          width: "100%",
        }}
      >
        {dias.map((date, index) => {
          if (!date) return <div key={index} style={{ minHeight: 0 }}></div>;

          const eventosDoDia = recebimentos.filter((r) =>
            dayjs(r.DataPrevista).isSame(date, "day")
          );

          const isToday = dayjs().isSame(date, "day");
          const dayOfWeek = date.format("dddd");
          const eventosAgrupados = getEventosAgrupados(eventosDoDia);
          const totalEventos = eventosDoDia.length;

          return (
            <Tooltip
              key={index}
              title={`${dayOfWeek}, ${date.format("DD/MM/YYYY")}`}
              arrow
              disableHoverListener={eventosDoDia.length > 0}
            >
              <div
                onClick={() => handleDayClick(date)}
                onMouseEnter={(e) => {
                  handleDayHover(index);
                  eventosDoDia.length > 0 &&
                    handlePopoverOpen(e, date, eventosDoDia);
                }}
                onMouseLeave={() => {
                  handleDayLeave();
                  handlePopoverClose();
                }}
                style={{
                  padding: "8px 4px",
                  borderRadius: 6,
                  minHeight: 52,
                  position: "relative",
                  backgroundColor: isToday ? "#1f4680" : "transparent",
                  border: isToday ? "none" : "1px solid #e0e0e0",
                  cursor: eventosDoDia.length > 0 ? "pointer" : "default",
                  opacity: loading ? 0.7 : 1,
                  transition: "all 0.2s ease",
                  aspectRatio: "1/1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  boxShadow:
                    hoveredDay === index ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    color: isToday ? "white" : "#333",
                    fontWeight: isToday ? 600 : 400,
                    fontSize: 12,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "100%",
                    textAlign: "center",
                  }}
                >
                  {date.date()}
                </div>
                {loading ? (
                  <div
                    style={{
                      marginTop: 6,
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        width: "60%",
                        height: 6,
                        backgroundColor: "#f0f0f0",
                        borderRadius: 3,
                        animation: "pulse 1.5s infinite ease-in-out",
                      }}
                    />
                    <div
                      style={{
                        width: "40%",
                        height: 6,
                        backgroundColor: "#f0f0f0",
                        borderRadius: 3,
                        animation: "pulse 1.5s infinite ease-in-out",
                        animationDelay: "0.2s",
                      }}
                    />
                    <div
                      style={{
                        width: "50%",
                        height: 6,
                        backgroundColor: "#f0f0f0",
                        borderRadius: 3,
                        animation: "pulse 1.5s infinite ease-in-out",
                        animationDelay: "0.4s",
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 3,
                      marginTop: 6,
                      justifyContent: "center",
                      maxWidth: "100%",
                      alignItems: "center",
                    }}
                  >
                    {eventosAgrupados.slice(0, 3).map((evento, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            backgroundColor: statusColors[evento.status],
                            borderRadius: "50%",
                            opacity: isToday ? 0.9 : 1,
                          }}
                        />
                        {evento.count > 1 && (
                          <span
                            style={{
                              fontSize: 9,
                              color: isToday ? "white" : "#666",
                              fontWeight: "bold",
                            }}
                          >
                            {evento.count}
                          </span>
                        )}
                      </div>
                    ))}
                    {eventosAgrupados.length > 3 && (
                      <span
                        style={{
                          fontSize: 9,
                          color: isToday ? "white" : "#666",
                          fontWeight: "bold",
                        }}
                      >
                        +{eventosAgrupados.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Tooltip>
          );
        })}
      </div>

      {/* Popper resumo - Mantido original */}
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom"
        disablePortal
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
        ]}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            boxShadow: 3,
            borderRadius: 2,
            p: 2,
            width: 280,
            zIndex: 9999,
            border: "1px solid #e0e0e0",
          }}
        >
          {popoverData && (
            <>
              <h5
                style={{
                  marginTop: 0,
                  color: "#1f4680",
                  fontSize: 14,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {popoverData.date.format("dddd, DD [de] MMMM [de] YYYY")}
              </h5>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  marginTop: 8,
                }}
              >
                {Object.entries(popoverData.resumo).map(
                  ([status, { count, total }]) => (
                    <div
                      key={status}
                      style={{
                        background: "#f7f7f7",
                        borderLeft: `4px solid ${statusColors[status as Recebimento["Status"]
                          ]}`,
                        padding: "6px 10px",
                        borderRadius: 4,
                        fontSize: 13,
                      }}
                    >
                      <strong style={{ color: "#1f4680" }}>{status}</strong>
                      <br />
                      {count} recebimento(s)
                      <br />
                      Total: {formatarParaReais(total)}
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </Box>
      </Popper>

      {/* Estilos para a animação de loading */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 0.6; }
            50% { opacity: 0.3; }
            100% { opacity: 0.6; }
          }
        `}
      </style>
    </div>
  );
};